import type {
  ISODate,
  ISchedulerDependency,
  TaskId,
  LinkType,
} from '@interfaces/IScheduler';
import type { CascadeFreeze } from './freeze';
import { childIndex, makeRollup } from './rollup';
import { addDays, daysBetween } from './timeScale';
import { isScheduled } from './taskDates';
import {
  type WorkCalendar,
  ALL_DAYS_WORKING,
  addWorkingDays,
  workingDaysBetween,
} from './calendar';

/**
 * The structural slice of a task cascade actually touches: identity, the WBS
 * parent link, and the inclusive span. Generic over this so it operates on
 * either full `ISchedulerTask`s or the plan-only tasks stored in `bySite` —
 * execution fields (status/progress/locations/log) are never read here.
 */
const EMPTY_FROZEN: ReadonlyMap<TaskId, CascadeFreeze> = new Map();

interface ICascadeTask {
  id: TaskId;
  parentId?: TaskId | null;
  start: ISODate;
  end: ISODate;
}

/**
 * When a non-trivial calendar is supplied, advance by WORKING days; when it is
 * the default, fall back to the plain `addDays` path so existing behaviour is
 * preserved byte-for-byte.
 */
function advancer(calendar: WorkCalendar): (date: string, days: number) => string {
  if (calendar === ALL_DAYS_WORKING) return addDays;
  return (date, days) => addWorkingDays(date, days, calendar);
}

// Earliest start a successor may take given ONE predecessor edge, computed from
// the predecessor's EFFECTIVE (rolled-up) span. `succDurationInclusive` is the
// successor's inclusive calendar-day span, used to back-compute the end-anchored
// (FF/SF) constraints.
function minSuccStartFromEdge(
  predStart: string,
  predEnd: string,
  edge: { lag: number; type: LinkType },
  succDurationInclusive: number,
  advance: (date: string, days: number) => string,
): string {
  switch (edge.type) {
    // FS with lag 0 lets the successor start ON the predecessor's finish day —
    // finish and start may share a day instead of forcing a one-day gap. This
    // matches the other three link types (all lag-relative, no built-in gap)
    // and the Genba import convention, where a chained task starts the day its
    // predecessor ends. A positive lag adds real gap days on top.
    case 'FS':
      return advance(predEnd, edge.lag);
    case 'SS':
      return advance(predStart, edge.lag);
    case 'FF': {
      const minEnd = advance(predEnd, edge.lag);
      return addDays(minEnd, -(succDurationInclusive - 1));
    }
    case 'SF': {
      const minEnd = advance(predStart, edge.lag);
      return addDays(minEnd, -(succDurationInclusive - 1));
    }
  }
}

/**
 * Cascade is PUSH-ONLY by default: a successor already past its constraint has
 * slack (manual placement) and stays put. `previousDeps` — the dependency list
 * as it was BEFORE the triggering edit — opts into pull semantics for
 * dependency edits: a successor that sat exactly on its old constraint is
 * "pinned" to it, so when the edited link now allows an earlier date (lag
 * decreased, type relaxed) the successor is pulled back to the new constraint.
 * This makes a lag decrease cascade backward the same way a lag increase
 * cascades forward, transitively — each pulled task re-anchors its own pinned
 * successors — while manually-slacked tasks still never move.
 *
 * `previousTasks` does the same for a DATE edit: the spans as they were before
 * it (by default, `tasks` itself — the reducers apply the edit and cascade from
 * the result). Given the pre-edit spans and the unchanged dependency list, a
 * task finishing earlier pulls the successors pinned to its old end back with
 * it (2026-09-22 "done on the day" amendment).
 *
 * `frozen` marks the tasks the field has already been observed working on
 * (docs/specs/2026-09-20-observed-work-is-frozen-amendment.md, decision 3). They
 * are ANCHORS: the cascade flows around them rather than through them. `all`
 * never moves at all; `start` keeps its start and may only have its END pushed,
 * by an end-anchored (FF/SF) link. A start-anchored (FS/SS) constraint on a
 * pinned task cannot be met, so the task stays put and the cascade terminates
 * there — its own successors were constrained on it and it did not move. The
 * link is then left visibly unsatisfied, which is the honest outcome: a
 * predecessor finishing after its successor started is a real thing on a real
 * site. A frozen task's own dates still constrain ITS successors — a task that
 * finished late must still push what follows it.
 */
export function cascade<T extends ICascadeTask>(
  tasks: T[],
  deps: ISchedulerDependency[],
  changedIds: TaskId[],
  calendar: WorkCalendar = ALL_DAYS_WORKING,
  previousDeps?: ISchedulerDependency[],
  frozen: ReadonlyMap<TaskId, CascadeFreeze> = EMPTY_FROZEN,
  previousTasks: readonly T[] = tasks,
): T[] {
  if (changedIds.length === 0) return tasks;

  const byId = new Map(tasks.map((task) => [task.id, { ...task }]));

  // A task with ≥1 child is a SUMMARY: its span is derived from its descendant
  // leaves, so it is never dated directly — a dependency that would push it
  // later instead shifts its whole subtree, and a dependency that reads it (as a
  // predecessor) reads its rolled-up span. With no children the index is empty
  // and this whole layer is inert, so flat graphs cascade exactly as before.
  const childIds = childIndex(byId.values());
  const isSummary = (id: TaskId): boolean => childIds.has(id);
  // Every leaf this pass moves is reported through `leafMoved`, so the cached
  // roll-ups of the phases above it are recomputed on their next read.
  const { effStart, effEnd, leafMoved } = makeRollup(childIds, (id) => byId.get(id)!);
  // An unscheduled task (no dates) takes no part in the cascade: it neither
  // constrains its successors nor is moved by its predecessors.
  const dated = (id: TaskId): boolean => byId.has(id) && isScheduled({ start: effStart(id), end: effEnd(id) });

  // Shift every descendant LEAF of `id` by `deltaDays` calendar days, preserving
  // each leaf's duration and the subtree's internal gaps (a "move the phase"
  // shift, MS-Project style). Uses plain addDays — not the work-calendar advance
  // — so the subtree keeps its exact shape; the constraint that triggered the
  // shift was already calendar-resolved. Collects the moved leaf ids into `out`.
  function shiftLeaves(id: TaskId, deltaDays: number, out: TaskId[]): void {
    const kids = childIds.get(id);
    if (!kids) {
      // An observed leaf is an anchor: dragging the phase it sits in moves
      // everything around it and leaves it where the field put it. The summary's
      // rolled-up span may then miss its own constraint — that is the honest
      // outcome, not a bug to paper over.
      if (frozen.has(id)) return;
      const leaf = byId.get(id)!;
      // An unscheduled leaf has no dates to shift — it rides along undated.
      if (!isScheduled(leaf)) return;
      leaf.start = addDays(leaf.start, deltaDays);
      leaf.end = addDays(leaf.end, deltaDays);
      leafMoved(id);
      out.push(id);
      return;
    }
    for (const kid of kids) shiftLeaves(kid, deltaDays, out);
  }

  // Build forward adjacency for BFS propagation.
  const forward = new Map<TaskId, { to: TaskId; lag: number; type: LinkType }[]>();
  for (const d of deps) {
    if (!forward.has(d.predecessorId)) forward.set(d.predecessorId, []);
    forward.get(d.predecessorId)!.push({ to: d.successorId, lag: d.lagDays, type: d.type });
  }

  // Build reverse adjacency for the per-successor max() check.
  const reverse = new Map<TaskId, { from: TaskId; lag: number; type: LinkType }[]>();
  for (const d of deps) {
    if (!reverse.has(d.successorId)) reverse.set(d.successorId, []);
    reverse.get(d.successorId)!.push({ from: d.predecessorId, lag: d.lagDays, type: d.type });
  }

  // Pre-pass snapshot for the pinned check (pull semantics, header comment).
  // `previousTasks` holds untouched objects, so original effective spans stay
  // readable mid-pass even after byId clones have been shifted.
  const origById = new Map(previousTasks.map((task) => [task.id, task]));
  const { effStart: effStartOrig, effEnd: effEndOrig } = makeRollup(childIds, (id) => origById.get(id)!);
  const reversePrev = previousDeps
    ? (() => {
        const map = new Map<TaskId, { from: TaskId; lag: number; type: LinkType }[]>();
        for (const d of previousDeps) {
          if (!map.has(d.successorId)) map.set(d.successorId, []);
          map.get(d.successorId)!.push({ from: d.predecessorId, lag: d.lagDays, type: d.type });
        }
        return map;
      })()
    : null;

  const advance = advancer(calendar);
  const useCalendar = calendar !== ALL_DAYS_WORKING;

  function taskDuration(task: ICascadeTask): number {
    return useCalendar
      ? workingDaysBetween(task.start, task.end, calendar) - 1
      : daysBetween(task.start, task.end);
  }

  // Enqueue a node plus every ancestor: moving a leaf changes its ancestor
  // summaries' rolled-up spans, so any dependency OUT of an ancestor must be
  // re-evaluated. Walking forward edges of an ancestor that didn't actually
  // change is a cheap no-op (its successors stay put), so over-enqueuing is safe.
  const queue: TaskId[] = [];
  const enqueueWithAncestors = (id: TaskId): void => {
    queue.push(id);
    let parent = byId.get(id)?.parentId ?? null;
    while (parent != null) {
      queue.push(parent);
      parent = byId.get(parent)?.parentId ?? null;
    }
  };
  for (const id of changedIds) enqueueWithAncestors(id);
  const visited = new Set<TaskId>();

  // Head-pointer dequeue (Array.shift() is O(n)); keeps BFS propagation linear
  // in the dependency fan-out. Each successor is modified at most once (`visited`)
  // so the pass terminates even though shifts re-enqueue ancestors/leaves.
  let head = 0;
  while (head < queue.length) {
    const id = queue[head++]!;
    for (const edge of forward.get(id) ?? []) {
      const succId = edge.to;
      if (visited.has(succId)) continue;
      if (!dated(succId)) continue;
      // A closed record never moves. It stays in `reverse` though, so it still
      // constrains ITS successors from wherever the field left it.
      const freeze = frozen.get(succId);
      if (freeze === 'all') continue;

      const succEffStart = effStart(succId);
      const succDurationInclusive = daysBetween(succEffStart, effEnd(succId)) + 1;

      // Compute minStart across ALL incoming edges (not just the one we walked),
      // reading each predecessor's effective span.
      let minStart: string | null = null;
      for (const inc of reverse.get(succId) ?? []) {
        if (!dated(inc.from)) continue;
        const candidate = minSuccStartFromEdge(effStart(inc.from), effEnd(inc.from), inc, succDurationInclusive, advance);
        if (minStart === null || daysBetween(minStart, candidate) > 0) {
          minStart = candidate;
        }
      }
      // Pull check (see header comment): with `previousDeps` supplied, a
      // successor whose ORIGINAL start sat exactly on its OLD constraint is
      // pinned — re-anchor it when the constraint moved earlier. Both sides of
      // the comparison use original spans / old edges so a pull earlier in this
      // pass can't misclassify a downstream task's slack.
      let mustPull = false;
      if (minStart !== null && reversePrev && daysBetween(succEffStart, minStart) < 0) {
        const origSpanInclusive = daysBetween(effStartOrig(succId), effEndOrig(succId)) + 1;
        let oldMinStart: string | null = null;
        for (const inc of reversePrev.get(succId) ?? []) {
          if (!origById.has(inc.from) || !isScheduled({ start: effStartOrig(inc.from), end: effEndOrig(inc.from) })) continue;
          const candidate = minSuccStartFromEdge(effStartOrig(inc.from), effEndOrig(inc.from), inc, origSpanInclusive, advance);
          if (oldMinStart === null || daysBetween(oldMinStart, candidate) > 0) {
            oldMinStart = candidate;
          }
        }
        mustPull = oldMinStart !== null && effStartOrig(succId) === oldMinStart;
      }
      if (minStart && (daysBetween(succEffStart, minStart) > 0 || mustPull)) {
        visited.add(succId);
        if (isSummary(succId)) {
          // A summary can't be dated directly — shift its whole subtree so its
          // rolled-up start meets the constraint. Propagate from the summary,
          // every moved leaf, and (via enqueueWithAncestors) their ancestors.
          const moved: TaskId[] = [];
          shiftLeaves(succId, daysBetween(succEffStart, minStart), moved);
          enqueueWithAncestors(succId);
          for (const movedId of moved) enqueueWithAncestors(movedId);
        } else if (freeze === 'start') {
          // The start is an observation. Only an END-anchored link can still be
          // satisfied, by stretching the task rather than sliding it; a
          // start-anchored one is simply left unmet. Push-only — a pinned task's
          // end is never pulled earlier by a relaxed constraint. `minEnd` beats
          // the current end, which is at or after the start, so the span stays
          // valid without a floor.
          const succ = byId.get(succId)!;
          let minEnd: string | null = null;
          for (const inc of reverse.get(succId) ?? []) {
            if (inc.type !== 'FF' && inc.type !== 'SF') continue;
            if (!dated(inc.from)) continue;
            const anchor = inc.type === 'FF' ? effEnd(inc.from) : effStart(inc.from);
            const candidate = advance(anchor, inc.lag);
            if (minEnd === null || daysBetween(minEnd, candidate) > 0) minEnd = candidate;
          }
          if (minEnd !== null && daysBetween(succ.end, minEnd) > 0) {
            succ.end = minEnd;
            leafMoved(succId);
            enqueueWithAncestors(succId);
          }
        } else {
          const succ = byId.get(succId)!;
          const duration = taskDuration(succ);
          succ.start = minStart;
          succ.end = advance(minStart, duration);
          leafMoved(succId);
          enqueueWithAncestors(succId);
        }
      }
    }
  }

  // Preserve the original task object reference for every task whose start and
  // end are unchanged. cascade only ever mutates those two fields, so an
  // unchanged task is byte-for-byte identical to its original. Returning `orig`
  // instead of the mutated clone lets React.memo on task bars skip re-renders
  // when only unrelated tasks moved — every unchanged bar keeps its previous
  // identity. Inside Immer, `orig` is a draft; returning it for an unchanged
  // task lets Immer preserve the underlying structural identity on finalize.
  return tasks.map((orig) => {
    const next = byId.get(orig.id)!;
    return next.start === orig.start && next.end === orig.end ? orig : next;
  });
}

/**
 * The dependencies the current dates no longer satisfy.
 *
 * The cascade stops at observed work (see the header), so a predecessor that
 * slips past a successor the field has already started leaves the link unmet.
 * That is shown rather than hidden: a predecessor finishing after its successor
 * began is a real thing on a real site, and it is exactly the variance a planner
 * needs to see. Pre-existing violations in imported data surface the same way.
 *
 * Same edge math and same roll-up as the cascade itself, so the arrow agrees
 * with the reflow that produced it.
 */
export function violatedDependencies<T extends ICascadeTask>(
  tasks: readonly T[],
  deps: readonly ISchedulerDependency[],
  calendar: WorkCalendar = ALL_DAYS_WORKING,
): Set<string> {
  const byId = new Map(tasks.map((task) => [task.id, task] as const));
  const childIds = childIndex(tasks);
  const { effStart, effEnd } = makeRollup(childIds, (id) => byId.get(id)!);
  const advance = advancer(calendar);

  const violated = new Set<string>();
  const dated = (id: TaskId): boolean => byId.has(id) && isScheduled({ start: effStart(id), end: effEnd(id) });
  for (const dep of deps) {
    // A link with an undated end has nothing to violate.
    if (!dated(dep.predecessorId) || !dated(dep.successorId)) continue;
    const succStart = effStart(dep.successorId);
    const succSpan = daysBetween(succStart, effEnd(dep.successorId)) + 1;
    const minStart = minSuccStartFromEdge(
      effStart(dep.predecessorId),
      effEnd(dep.predecessorId),
      { lag: dep.lagDays, type: dep.type },
      succSpan,
      advance,
    );
    if (daysBetween(succStart, minStart) > 0) violated.add(dep.id);
  }
  return violated;
}
